/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-classes-per-file */
import * as React from "react";
import { connect } from "react-redux";
import {
  InlineNotification,
  Loading,
  Modal,
  ProgressIndicator,
  ProgressStep,
} from "carbon-components-react";

import Breadcrumbs from "./Breadcrumbs";
import EditorPage from "./EditorPage";
import ImportData from "./ImportData";
import { FIELD_TYPE, getFieldDef } from "./wsDefs";
import { IWorksheetState } from "./interfaces";
import { getWsById, getWsByName } from "./wsHelpers";
import { mapStateToProps, mapDispatchToProps, mergeProps } from "./selectors";

/* worksheet editor */

const initProps = ({ worksheets, openToWsName, openToWsId }) => {
  let currentWs;
  if (openToWsId) {
    currentWs = getWsById(worksheets, openToWsId);
  } else {
    currentWs = getWsByName(worksheets, openToWsName);
  }

  return {
    currentWsId: currentWs.id,
    wsIdPath: [currentWs.id],
  };
};

interface IEditorProps {
  appId: number;
  openToWsName: string;
  openToWsId?: string;
  worksheets: IWorksheetState[];
  actions: {
    wsCreate: Function;
    wsImport: Function;
    wsFetch: Function;
    wsUpdate: Function;
    wsDeleteFromField: Function;
  };
  onUpdate?: Function;
  hideNavigation?: boolean;
  showChildrenInline?: boolean;
  showHidden?: boolean;
  readOnly?: boolean;
}

class WorksheetEditor extends React.Component<IEditorProps, any> {
  constructor(props) {
    super(props);
    this.state = initProps(props);

    this.addWsToParent = this.addWsToParent.bind(this);
    this.deleteWsFromParent = this.deleteWsFromParent.bind(this);
    this.fieldUpdate = this.fieldUpdate.bind(this);
    this.importData = this.importData.bind(this);
    this.setCurrentWsId = this.setCurrentWsId.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.appId !== this.props.appId) {
      this.setState(initProps(nextProps));
    }
  }

  setCurrentWsId(nextWsId: string) {
    const { wsIdPath } = this.state;

    const idx = wsIdPath.indexOf(nextWsId);

    let path;
    if (idx === -1) {
      path = [...wsIdPath, nextWsId];
    } else {
      path = wsIdPath.slice(0, idx + 1);
    }

    this.setState({
      currentWsId: nextWsId,
      wsIdPath: path,
    });
  }

  fieldUpdate(wsId, field, value) {
    const { worksheets, actions, onUpdate } = this.props;

    const ws = getWsById(worksheets, wsId);
    if (!ws) {
      throw new Error(`cannot find ${wsId} to update ${field}`);
    }

    return actions.wsUpdate(wsId, ws.name, ws.instance.version, field, value).then(() => {
      if (onUpdate) {
        onUpdate();
      }
    });
  }

  // TODO: jjw: move to actions.ts as compound ops

  addWsToParent(wsName, parentId, parentField) {
    const { worksheets, actions } = this.props;

    const parent = getWsById(worksheets, parentId);
    if (!parent) {
      throw new Error(`cannot find ${parentId} to add worksheet`);
    }

    const fieldDef = getFieldDef(parent.name, parentField);
    if (fieldDef.type !== FIELD_TYPE.WS_SLICE && fieldDef.type !== FIELD_TYPE.WS) {
      throw new Error(`cannot add worksheet to ${parentField} on ${parent.name}`);
    }

    return actions
      .wsCreate(wsName)
      .then(({ worksheet }) =>
        actions.wsUpdate(parentId, parent.name, parent.instance.version, parentField, worksheet.id),
      );
  }

  deleteWsFromParent(wsId, parentId, parentField) {
    const { worksheets, actions } = this.props;

    const parent = getWsById(worksheets, parentId);
    if (!parent) {
      throw new Error(`cannot find ${parentId} to delete worksheet`);
    }

    const fieldDef = getFieldDef(parent.name, parentField);
    if (fieldDef.type !== FIELD_TYPE.WS_SLICE) {
      throw new Error(`cannot delete worksheet from ${parentField} on ${parent.name}`);
    }

    // FIXME: jjw: ok for uuids, but not generally safe for values in slices
    const idx = parent.instance[parentField].indexOf(wsId);
    if (idx === -1) {
      throw new Error(`worksheet ${wsId} not found to delete in ${parentField} on ${parentId}`);
    }

    return actions.wsDeleteFromField(parentId, parent.instance.version, parentField, idx);
  }

  importData(wsName, wsId) {
    const { actions } = this.props;

    return actions.wsImport(wsName, wsId);
  }

  render() {
    const { worksheets, hideNavigation, showChildrenInline, showHidden, readOnly } = this.props;
    const { currentWsId, wsIdPath } = this.state;

    const ws = getWsById(worksheets, currentWsId);
    if (!ws) {
      throw new Error(`WorksheetsEditor failed: cannot find ${currentWsId}`);
    }

    return (
      <div>
        {!hideNavigation && (
          <Breadcrumbs
            worksheets={worksheets}
            wsIdPath={wsIdPath}
            setCurrentWsId={this.setCurrentWsId}
          />
        )}
        <ImportData
          wsName={ws.name}
          onClick={() => {
            this.importData(ws.name, currentWsId);
          }}
        />
        <EditorPage
          worksheets={worksheets}
          wsId={currentWsId}
          fieldUpdate={this.fieldUpdate}
          setCurrentWsId={this.setCurrentWsId}
          addWsToParent={this.addWsToParent}
          deleteWsFromParent={this.deleteWsFromParent}
          showChildrenInline={showChildrenInline}
          showHidden={showHidden}
          readOnly={readOnly}
        />
      </div>
    );
  }
}

interface ILoaderProps {
  appId: number;
  loanFileWsId: string;
  openToWsName: string; // open/create a singleton of this type on the loan file
  openToWsId?: string; // if set, open instead to this id of type openToWsName
  worksheets: IWorksheetState[];
  actions: {
    initLoanFileWs: Function;
    initWsByName: Function;
    initWsById: Function;
    wsCreate: Function;
    wsImport: Function;
    wsFetch: Function;
    wsUpdate: Function;
    wsDeleteFromField: Function;
  };
  hideNavigation?: boolean;
  showChildrenInline?: boolean;
  showLoadingModal?: boolean;
}

// This enum provides the "current step" index for the ProgressIndicator
// Striking a balance between "too much technical depth" and "enough information" is tricky
// 3-4 steps shows progress while not being overwhelming or verbose.
enum WsLoadingPhase {
  InitializingLFWS = 0,
  LFWSLoaded = 1,
  LoadingSpecificWS = 2,
}

class WorksheetLoader extends React.Component<ILoaderProps, any> {
  constructor(props) {
    super(props);

    const { openToWsId, openToWsName } = this.props;

    const progressSteps = [
      "Initializing loan file worksheet",
      "Loan file worksheet loaded",
      openToWsId ? `Loading worksheet ID ${openToWsId}` : `Loading worksheet "${openToWsName}"`,
    ];

    this.state = {
      loadingIndex: WsLoadingPhase.InitializingLFWS,
      progressSteps,
    };
  }

  componentDidMount() {
    const { loanFileWsId, actions } = this.props;

    actions.initLoanFileWs(loanFileWsId);
  }

  updateLoadingPhase() {
    const { worksheets, openToWsName, openToWsId } = this.props;
    const { loadingIndex } = this.state;

    const isLoadedLFWS =
      worksheets.length === 1 && loadingIndex === WsLoadingPhase.InitializingLFWS;

    // If the LFWS is loaded, mark phase as complete
    if (isLoadedLFWS) {
      this.setLoadingPhase(WsLoadingPhase.LFWSLoaded);
    }

    const isLoadingSpecificWS =
      worksheets.length === 1 && loadingIndex === WsLoadingPhase.LFWSLoaded;

    // If the LFWS is loaded, begin next step
    if (isLoadingSpecificWS && openToWsId) {
      if (!getWsById(worksheets, openToWsId)) {
        this.setLoadingPhase(WsLoadingPhase.LoadingSpecificWS);
        return;
      }
    }

    // If the LFWS is loaded, begin next step
    if (isLoadingSpecificWS && openToWsName) {
      if (!getWsByName(worksheets, openToWsName)) {
        this.setLoadingPhase(WsLoadingPhase.LoadingSpecificWS);
      }
    }
  }

  componentDidUpdate() {
    this.updateLoadingPhase();
  }

  setLoadingPhase = (phase: WsLoadingPhase) => {
    const { showLoadingModal } = this.props;
    if (showLoadingModal) {
      this.setState({
        loadingIndex: phase,
      });
    }
  };

  componentWillReceiveProps(nextProps) {
    const { openToWsName, openToWsId, actions } = nextProps;

    // both actions return promises that we resolve/reject to be semantically
    // correct, but we don't do anything with the failure, so we need to catch
    if (openToWsId) {
      actions.initWsById(openToWsName, openToWsId).catch(() => {
        /* nop */
      });
    } else {
      actions.initWsByName(openToWsName).catch(() => {
        /* nop */
      });
    }
  }

  renderLoadingModal = () => {
    const { loadingIndex, progressSteps } = this.state;
    return (
      <Modal modalHeading="Loading worksheet..." passiveModal open>
        <InlineNotification
          hideCloseButton
          lowContrast
          kind="info"
          notificationType="inline"
          role="alert"
          title="Note: "
          subtitle="This may take a while - please do not refresh the page."
        />
        <div
          style={{
            display: "flex",
            width: "100%",
          }}
        >
          <ProgressIndicator currentIndex={loadingIndex} vertical>
            {progressSteps.map((p, index) => (
              <ProgressStep key={`${p}-progress-step`} index={index} label={p} description={p} />
            ))}
          </ProgressIndicator>
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              width: "450px",
            }}
          >
            <Loading active withOverlay={false} />
          </div>
        </div>
      </Modal>
    );
  };

  render() {
    const { openToWsName, openToWsId, worksheets, showLoadingModal } = this.props;

    const openToWs = openToWsId
      ? getWsById(worksheets, openToWsId)
      : getWsByName(worksheets, openToWsName);

    const loaded = openToWs && openToWs.instance;

    return (
      <>
        {!loaded && showLoadingModal && this.renderLoadingModal()}
        {!loaded && !showLoadingModal && <Loading withOverlay={false} />}
        {loaded && <WorksheetEditor {...this.props} />}
      </>
    );
  }
}

export default connect<any, any, any>(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(WorksheetLoader);
